import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AuthService } from '../../services';
import { MatSnackBar as MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { Account } from '../../models';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../confirm-dialog/confirm-dialog.component';
import { ProjectsService } from '../../../modules/projects/core/projects.service';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import * as fromRoot from '../../../state/app.state';
import { FetchSearchDetailsRequest } from '../../core/shared.actions';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { searchResultsInfo } from '../../core/shared.selectors';
import { CommonService } from '../../services/common.service';
import { iconOf, matchedHintHtmlOf, SEARCH_ENTITIES, searchEntityBySearchType, SearchEntityConfig } from '../../constants/search-entities';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChatPanelComponent } from '../../../modules/project/components/chat-panel/chat-panel.component';

@Component({
    selector: 'app-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss', '../../../material/material.css'],
    animations: [
        trigger('searchSlideAnimation', [
            state('menu', style({
                opacity: 1,
                transform: 'translateX(0)',
            })),
            state('search', style({
                opacity: 1,
                transform: 'translateX(0)',
            })),
            transition('menu => search', [
                style({
                    opacity: 0,
                    transform: 'translateX(-50px)',
                }),
                animate('300ms ease-out'),
            ]),
            transition('search => menu', [
                style({
                    opacity: 0,
                    transform: 'translateX(50px)',
                }),
                animate('300ms ease-out'),
            ]),
        ]),
    ],
    standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  public activePath: string;
  public account: Account;
  currentPath = '';
  form = new FormGroup({
    searchText: new FormControl({ value: '', disabled: false }),
  });

  searchResultsList: any[] = [];
  // One row per entity type (registry order): the type's top-ranked result.
  previewResults: any[] = [];
  searchText: string;
  isOpen = false;
  searchResultsLoading = false;
  openSearchBar = false;
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;
  // The preview only belongs on screen while the user is typing a query.
  // Results also arrive from the /search page's own fetches, which must not
  // pop the dropdown open over the page.
  private previewRequested = false;
  actingRole: any = '';
  isChatEnabled = false;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private snackBar: MatSnackBar,
    private router: Router,
    public authService: AuthService,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    public dialog: MatDialog,
    private cdRef: ChangeDetectorRef,
    private store: Store<fromRoot.State>,
  ) {
  }

  @HostListener('document:keydown.escape')
  onEscapePress() {
    this.closePreview();
  }

  /** Template-facing alias for the backdrop click. */
  dismissPreview() {
    this.closePreview();
  }

  /** Dismiss the dropdown and stop an in-flight fetch from re-opening it. */
  private closePreview() {
    this.previewRequested = false;
    this.isOpen = false;
  }

  ngOnInit(): void {
    this.activePath = this.router.url;
    this.applyAccount(this.authService.getCurrentUser());

    // The boot-time refresh lands after first render; re-apply org flags and roles
    this.authService.accountRefreshed$
      .pipe(takeUntil(this.onDestroy))
      .subscribe(account => {
        this.applyAccount(account);
        this.cdRef.detectChanges();
      });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        // Landing anywhere (incl. a result's target page) dismisses the preview.
        this.previewRequested = false;
        this.isOpen = false;

        // Back on the results page: the "Back to search results" link is moot.
        if (event.urlAfterRedirects.split('?')[0] === '/search') {
          this.commonService.searchResultsUrl = null;
        }
      }

      if (event?.['routerEvent']?.url) {
        const url = (event?.['routerEvent']?.url.split('?')[0]).split('/')[1];
        this.currentPath = `/${url}`;
        this.cdRef.detectChanges();
      }
    });

    this.store.select(searchResultsInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));

          this.searchResultsLoading = data?.loading;
          this.searchResultsList = data?.searchResults;
          const typesWithResults = SEARCH_ENTITIES
            .filter(entity => data?.resultsByType?.[entity.searchType]?.length);
          // One top result per category; when only one category matched,
          // show its top 3 instead.
          this.previewResults = typesWithResults.length === 1
            ? data.resultsByType[typesWithResults[0].searchType].slice(0, 3)
            : typesWithResults.map(entity => data.resultsByType[entity.searchType][0]);

          if (this.searchResultsList?.length > 0 && this.previewRequested) {
            this.isOpen = true;
          }
        }
      });

    this.form.controls.searchText.valueChanges
      .pipe(
        debounceTime(1000),
        takeUntil(this.onDestroy),
      )
      .subscribe(searchText => {
        this.commonService.resetSearchContext.next(true);
        this.searchText = searchText;
        this.isOpen = true;
        this.router.navigate([], {
          queryParams: {
            searchText,
          },
          queryParamsHandling: 'merge',
        }).then(() => {
          if (searchText) {
            if (searchText?.length >= 3) {
              this.search();
            }
          }
        });
      });

    this.commonService.closeSearchPopup
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => {
        this.isOpen = false;
      });
  }

  logout() {
    this.authService.logout().subscribe({
      next: () => {
        this.snackBar.open('Logged out successfully.', '', {
          duration: 3000,
          panelClass: ['success'],
        });
        // Navigate first, clear state after: clearing the store while a page
        // like site-details is still mounted makes its selectors re-emit and
        // its own `router.navigate([], { queryParams… })` calls supersede the
        // in-flight /login navigation (NavigationCancel: "not equal to the
        // current navigation id"), leaving a logged-out user on the page.
        this.router.navigate(['/login']).then(() => this.commonService.clearAppState());
      },
      error: (resp: any) => {
        if (resp.error.error) {
          this.snackBar.open(resp.error.error, '', { duration: 5000 });
        }
      },
    });
  }

  navigate(path) {
    const isFormDirty = this.projectsService.isAnyFormDirty.getValue();

    if (isFormDirty) {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        disableClose: true,
        data: new ConfirmDialogModel(
          'Unsaved changes',
          `There are unsaved changes.<br/>Are you sure you want to discard?`,
        ),
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.router.navigate([path])
            .then(() => {
              this.projectsService.isAnyFormDirty.next(false);
            });
        }
      });
    } else {
      this.router.navigate([path]).then(() => {
        this.projectsService.closeRightPanel.next();
        this.projectsService.isAnyFormDirty.next(false);
      });
    }
  }

  search() {
    this.commonService.resetSearchContext.next(true);

    const { searchText } = this.form.getRawValue();
    const payload = {
      searchText,
    };
    if (searchText?.length >= 3) {
      this.previewRequested = true;
      this.isOpen = true;
      this.store.dispatch(FetchSearchDetailsRequest({ payload }));
    }
  }

  close() {
    this.form.reset();
    this.closePreview();
    this.onCloseClick();
  }

  seeAllResults() {
    const { searchText } = this.form.getRawValue();
    this.closePreview();
    this.commonService.closeSearchPopup.next(true);

    this.router.navigate(['/']).then(() => {
      this.router.navigate(['/search'], {
        queryParams: {
          searchText,
        },
      });
    });
  }

  configFor(result: any): SearchEntityConfig {
    return searchEntityBySearchType[result?.searchType];
  }

  iconFor(result: any): string {
    return iconOf(this.configFor(result), result);
  }

  titleFor(result: any): string {
    return this.configFor(result)?.titleOf?.(result);
  }

  metaFor(result: any): string {
    return this.configFor(result)?.metaOf?.(result);
  }

  /** Typeahead sub-heading: entity category first, then the result meta. */
  subHeadingFor(result: any): string {
    const config = this.configFor(result);

    return [config?.singularLabel, config?.metaOf?.(result)]
      .filter(Boolean)
      .join(' . ');
  }

  matchInfoFor(result: any): string {
    return this.configFor(result)?.matchInfoOf?.(result);
  }

  matchedHintHtmlFor(result: any): string {
    return matchedHintHtmlOf(result);
  }

  /**
   * A preview row stands for its whole category: clicking lands on /search
   * with that category's tab selected. Record-level navigation happens on the
   * /search page cards.
   */
  openCategory(result: any) {
    const config = this.configFor(result);
    const { searchText } = this.form.getRawValue();

    this.closePreview();
    this.commonService.closeSearchPopup.next(true);

    this.router.navigate(['/']).then(() => {
      this.router.navigate(['/search'], {
        queryParams: {
          searchText,
          tab: config?.tabSlug,
        },
      });
    });
  }

  /** Set while the user is on a page they reached by opening a search result. */
  get backToResultsUrl(): string | null {
    return this.currentPath === '/search' ? null : this.commonService.searchResultsUrl;
  }

  backToResults() {
    const url = this.backToResultsUrl;
    if (url) {
      this.router.navigateByUrl(url);
    }
  }

  onSearchClick() {
    this.closePreview();
    this.openSearchBar = true;
    // The input lives in the @else branch, so it only exists after the next
    // change detection pass — focus it then, so typing can start right away.
    setTimeout(() => this.searchInput?.nativeElement?.focus());
  }

  onCloseClick() {
    this.openSearchBar = false;
  }

  openChat(): void {
    const url = this.router.url;
    let projectUuid: string = null;

    const outletMatch = url.match(/\/projects\/\(content:([^/)\s]+)/);
    if (outletMatch) {
      projectUuid = outletMatch[1];
    }

    if (!projectUuid) {
      const standardMatch = url.match(/\/projects\/([a-zA-Z0-9_-]{6,})(?:[?/#(]|$)/);
      if (standardMatch) {
        projectUuid = standardMatch[1];
      }
    }

    this.dialog.open(ChatPanelComponent, {
      position: { right: '0', top: '0' },
      width: '480px',
      height: '100vh',
      panelClass: 'chat-panel-dialog',
      data: { projectUuid },
      hasBackdrop: true,
      autoFocus: false,
    });
  }

  private applyAccount(account: Account) {
    this.account = account;

    // Check AI chat org flag — only show if explicitly enabled, default to hidden
    this.isChatEnabled = !!account?.organization?.ai_chat_enabled;

    // Restrict the nav only when every project role is reviewer / contractor-basic:
    // a PM who is also a reviewer on one project keeps Reports, Organization and search.
    const roles = account?.roles || [];
    const isRestricted = (role: { role?: string }) => role?.role === 'reviewer' || role?.role === 'contractor-basic';
    this.actingRole = roles.length > 0 && roles.every(isRestricted)
      ? 'contractor-basic'
      : '';
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
